import { Button } from '@components/ui'
import { renderData, renderHeaderTooltip } from '@utils/assessmentReportUtils'
import { Breadcrumb, Divider, Flex, Upload, UploadFile, message } from 'antd'
import { useMemo, useState } from 'react'

import { Link, useNavigate } from '@suntex.core/nav'
import {
  Entities_Team_CreateMultiplePlayers_Request,
  Entities_Team_UploadFile_Request,
  Entities_Team_UploadFile_StudentInfo,
  Entities_Team_ValidateMultiplePlayers_EntityResult,
  Entities_Team_ValidateMultiplePlayers_Request
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework } from '@suntex.fim/framework'
import { useTeamInfo } from '@suntex.fim/team'
import { ColumnType, Table, showErrorMessage } from '@suntex.fim/ui'

interface DuplicateEntry {
  error: string
  user: Entities_Team_UploadFile_StudentInfo
}
/**
 * returns JSX for team roster
 */
function TeamRosterAddMultiple() {
  const { api } = useFramework()
  const { login } = useAuth(isAuthenticated)
  const MAX_FILE = 1
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [displayTable, setDisplayTable] = useState(false)
  const [displayError, setDisplayError] = useState(false)

  const teamInfo = useTeamInfo()
  const [tableData, setTableData] = useState<
    Entities_Team_UploadFile_StudentInfo[] | undefined
  >([])
  const [updatedTableData, setUpdatedTableData] = useState<
    Entities_Team_UploadFile_StudentInfo[]
  >([])
  const [uploadedFile, setUploadedFile] = useState<UploadFile[]>([]) // Explicit type
  const [fileToUpload, setFileToUpload] = useState<File | null>(null) // Single file for API submission
  const [error, setError] = useState<
    Pick<Entities_Team_UploadFile_StudentInfo, 'id'>[]
  >([])
  const validateFile = (file: File) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ]

    if (!allowedTypes.includes(file.type)) {
      message.error('Invalid file type. Only Excel or CSV files are allowed.')
      return false
    }

    return true
  }

  const handleFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setUploadedFile(fileList) // Update file list
    if (fileList.length === 0) {
      setFileToUpload(null) // Clear file when no files are present
      return
    }

    const file = fileList[0].originFileObj
    if (file && validateFile(file)) {
      setFileToUpload(file) // Save the valid file for upload
    } else {
      setFileToUpload(null)
    }
  }

  // Define these outside the parent component
  const renderHeader = (value: string) => renderHeaderTooltip(value)

  const renderCell = (value: string) => {
    return <b>{renderData(value)}</b>
  }

  /**
   * handles table column mapping
   */
  const columns = useMemo<ColumnType<Entities_Team_UploadFile_StudentInfo>[]>(
    () => [
      {
        header: () => renderHeader('Display Name'),
        size: 50,
        accessorKey: 'displayName',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'displayName'
      },
      {
        header: () => renderHeader('First Name'),
        size: 50,
        accessorKey: 'firstName',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'firstName'
      },
      {
        header: () => renderHeader('Last Name'),
        size: 50,
        accessorKey: 'lastName',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'lastName'
      },
      {
        header: () => renderHeader('ID'),
        size: 50,
        accessorKey: 'id',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'id'
      },
      {
        header: () => renderHeader('Alt Login'),
        size: 50,
        accessorKey: 'altLogin',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'altLogin'
      },
      {
        header: () => renderHeader('Alt Password'),
        size: 50,
        accessorKey: 'altPassword',
        cell: (info) => renderCell(info.getValue<string>()),
        id: 'altPassword'
      }
    ],
    []
  )

  const handleUpload = async () => {
    if (!fileToUpload) {
      message.error('No file selected for upload.')
      return
    }

    setIsLoading(true)

    try {
      const fileDataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
        reader.readAsDataURL(fileToUpload)
      })

      const parts = fileDataBase64.split('base64,')
      const data = parts.length > 1 ? parts[1] : ''

      const fileParameter: Entities_Team_UploadFile_Request = {
        data,
        fileName: fileToUpload.name
      }

      const response = await api.post_Team_Upload(fileParameter)
      if (!response) {
        message.error('File upload failed.')
        setUploadedFile([]) // Clear file list
        setFileToUpload(null) // Reset selected file
      } else {
        message.success('File uploaded successfully.')
        setTableData(response.data) // Populate table data
        setDisplayTable(true)
      }
    } catch (error) {
      showErrorMessage('Failed to upload file', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile([]) // Clear file list
    setFileToUpload(null) // Reset selected file
  }

  const handleClose = () => {
    setUploadedFile([]) // Clear file list
    setFileToUpload(null) // Clear selected file for API submission
    setDisplayTable(false)
    setDisplayError(false)
  }
  const hasError = (
    data: Entities_Team_ValidateMultiplePlayers_EntityResult[]
  ) => {
    return data.filter((item) => item.status === false)
  }
  const isUnique = (
    users: Entities_Team_UploadFile_StudentInfo[]
  ): DuplicateEntry[] => {
    const ids = new Set<string | undefined>()
    const altCombinations = new Set<string>()
    const duplicates: DuplicateEntry[] = []

    users.forEach((user) => {
      const altCombination = `${user.altLogin}-${user.altPassword}`

      // Check for duplicate `id` or duplicate `altLogin` + `altPassword`
      if (ids.has(user.id) || altCombinations.has(altCombination)) {
        duplicates.push({
          error: ids.has(user.id)
            ? 'Duplicate ID'
            : 'Duplicate altLogin and altPassword combination',
          user
        })
      }

      // Add to sets for tracking
      if (user.id) ids.add(user.id)
      altCombinations.add(altCombination)
    })

    return duplicates
  }

  const handleSave = async () => {
    const errorData: Pick<Entities_Team_UploadFile_StudentInfo, 'id'>[] = []
    setDisplayError(true)
    const uniqueUser = isUnique(updatedTableData)
    if (uniqueUser.length === 0) {
      setIsLoading(true)
      const body = {
        loginId: login.id,
        allPlayerDetails: updatedTableData.map((player) => ({
          displayName: player.displayName,
          firstName: player.firstName,
          lastName: player.lastName,
          id: player.id,
          altLogin: player.altLogin,
          altPassword: player.altPassword
        }))
      } as Entities_Team_ValidateMultiplePlayers_Request
      const result = await api.post_Team_ValidateMultiplePlayers(body)
      const validateMultiplePlayersEntityResult = hasError(result.data || [])
      if (validateMultiplePlayersEntityResult.length > 0) {
        validateMultiplePlayersEntityResult.forEach((item) => {
          if (item.playerDetails?.id) {
            errorData.push({ id: item.playerDetails.id })
          }
          message.error(
            ` For Player ${item.playerDetails.displayName} the following error occurred: ${item.messages}`
          )
        })
        setError(errorData)
        setIsLoading(false)
      } else {
        const body = {
          players: updatedTableData.map((player) => ({
            teamId: teamInfo?.id,
            displayName: player.displayName,
            firstName: player.firstName,
            lastName: player.lastName,
            idNumber: player.id,
            altUser: player.altLogin,
            altPassword: player.altPassword
          }))
        } as Entities_Team_CreateMultiplePlayers_Request
        await api.post_Team_CreateMultiplePlayers(body)
        setDisplayTable(false)
        setIsLoading(false)
        message.success(
          `${updatedTableData.length} New students added successfully!`
        )
        navigate(`/team/${teamInfo?.id}/team-management/team-roster`)
      }
    } else {
      uniqueUser.forEach((item) => {
        if (item.user?.id) {
          errorData.push({ id: item.user.id })
        }
      })
      showErrorMessage(
        'Please make sure Id is unique and ALtLogin & AltPass combination should be unique for all students',
        errorData
      )
      setError(errorData)
    }
  }
  return (
    <Flex vertical gap="20px" className="teamroster-main">
      <Breadcrumb
        items={[
          {
            title: 'Team Roster'
          },
          {
            title: (
              <Link to={`/team/${teamInfo?.id}/team-management/team-roster`}>
                Add multiple students
              </Link>
            )
          }
        ]}
      />
      <Flex
        className="main-navigation__heading"
        align="center"
        justify="space-between"
      >
        <h3>Add Multiple Students</h3>
        {displayTable && (
          <Flex gap={6}>
            <Button shape="round" intent="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              shape="round"
              intent="primary"
              disabled={!displayTable}
              onClick={handleSave}
            >
              Save & Add Students
            </Button>
          </Flex>
        )}
      </Flex>
      {!displayTable && (
        <Flex vertical className="teamroster-dragger">
          <h4 className="text-sm font-medium teamroster-dragger__heading">
            Add Multiple records by uploading excel/csv files.
          </h4>
          <Upload
            className="teamroster-dragger__upload full-width"
            accept=".xlsx,.xls,.csv"
            onRemove={handleRemoveFile} // Clear state on file removal
            onChange={handleFileChange} // Update state on file change
            fileList={uploadedFile}
            maxCount={1}
            showUploadList={{
              showPreviewIcon: false,
              showRemoveIcon: true
            }}
            beforeUpload={(file) => {
              return validateFile(file) ? true : Upload.LIST_IGNORE // Validate file before upload
            }}
            customRequest={({ file, onSuccess }) => {
              // Simulate upload success to avoid showing the loader
              setTimeout(() => {
                if (onSuccess) onSuccess('ok', file)
              }, 0)
            }}
          >
            {uploadedFile.length < MAX_FILE ? (
              <Flex
                vertical
                align="center"
                className="teamroster-dragger__dropzone"
              >
                <p className="teamroster-dragger__title">Drag & Drop Files</p>
                <p className="teamroster-dragger__title">or</p>
                <p className="teamroster-dragger__title">
                  Click to Browse files.
                </p>
              </Flex>
            ) : null}
          </Upload>

          <p>
            .csv, .xlsx files supported <Divider type="vertical" />
            <a href="/add-students-sample.xlsx" download="add-students-sample">
              Download example file
            </a>
          </p>
          <Flex justify="end" className="teamroster-dragger__btn">
            <Button
              shape="round"
              intent="tertiary"
              key="submit"
              onClick={handleUpload}
              disabled={!fileToUpload}
            >
              Upload File
            </Button>
          </Flex>
        </Flex>
      )}
      {displayTable && (
        <Table<Entities_Team_UploadFile_StudentInfo>
          data={tableData || []}
          isLoading={isLoading}
          columns={columns}
          totalEntries={100}
          rowEditable
          setUpdatedTableData={setUpdatedTableData}
          error={error}
          displayError={displayError}
        />
      )}
    </Flex>
  )
}

export default TeamRosterAddMultiple
